import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Unit, UnitPayload, UnitType } from '../../types/catalog'
import { FormField, Input, Select, Row } from '../../components/ui/FormField'
import { tokens } from '../../styles/tokens'

interface UnitTranslationWithAbbr {
  locale: string
  name: string
  abbreviation: string
}

interface Props {
  initial?: Unit | null
  onChange: (payload: UnitPayload) => void
}

const LocaleTag = styled.span`
  display: inline-block;
  background: ${tokens.purple100};
  color: ${tokens.purple600};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 4px;
  margin-bottom: 8px;
`

const LOCALES = ['en', 'es']

export function UnitForm({ initial, onChange }: Props) {
  const [code, setCode] = useState(initial?.code ?? '')
  const [type, setType] = useState<UnitType>(initial?.type ?? 'weight')
  const [gramsEquivalent, setGramsEquivalent] = useState<string>(
    initial?.gramsEquivalent?.toString() ?? ''
  )
  const [position, setPosition] = useState(initial?.position ?? 0)
  const [translations, setTranslations] = useState<UnitTranslationWithAbbr[]>(
    LOCALES.map(locale => {
      const existing = initial?.translations.find(t => t.locale === locale)
      return { locale, name: existing?.name ?? '', abbreviation: existing?.abbreviation ?? '' }
    })
  )

  const updateTranslation = (locale: string, field: 'name' | 'abbreviation', value: string) => {
    setTranslations(prev => prev.map(t => (t.locale === locale ? { ...t, [field]: value } : t)))
  }

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    onChangeRef.current({
      code,
      type,
      gramsEquivalent: gramsEquivalent !== '' ? Number(gramsEquivalent) : null,
      position,
      active: true,
      translations,
    })
  }, [code, type, gramsEquivalent, position, translations])

  return (
    <>
      <Row>
        <FormField label="Code" hint="e.g. GRAM">
          <Input
            placeholder="GRAM"
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
          />
        </FormField>
        <FormField label="Type">
          <Select value={type} onChange={e => setType(e.target.value as UnitType)}>
            <option value="weight">Weight</option>
            <option value="volume">Volume</option>
            <option value="piece">Piece</option>
          </Select>
        </FormField>
      </Row>

      <Row>
        <FormField label="Grams equivalent" hint="Leave empty if not convertible">
          <Input
            type="number"
            placeholder="e.g. 236.6 for 1 cup"
            value={gramsEquivalent}
            onChange={e => setGramsEquivalent(e.target.value)}
          />
        </FormField>
        <FormField label="Position">
          <Input
            type="number"
            value={position}
            onChange={e => setPosition(Number(e.target.value))}
          />
        </FormField>
      </Row>

      {LOCALES.map(locale => (
        <div key={locale}>
          <LocaleTag>{locale === 'en' ? 'English' : 'Spanish'}</LocaleTag>
          <Row>
            <FormField label="Name">
              <Input
                placeholder={locale === 'en' ? 'e.g. Cup' : 'e.g. Taza'}
                value={translations.find(t => t.locale === locale)?.name ?? ''}
                onChange={e => updateTranslation(locale, 'name', e.target.value)}
              />
            </FormField>
            <FormField label="Abbreviation">
              <Input
                placeholder={locale === 'en' ? 'cup' : 'tza.'}
                value={translations.find(t => t.locale === locale)?.abbreviation ?? ''}
                onChange={e => updateTranslation(locale, 'abbreviation', e.target.value)}
              />
            </FormField>
          </Row>
        </div>
      ))}
    </>
  )
}
